import { memo } from "react";
import { Star } from "lucide-react";

interface GoogleReviewsBadgeProps {
  rating?: number;
  reviewCount?: number;
  variant?: "compact" | "full";
  className?: string;
}

const GoogleReviewsBadge = memo(({
  rating = 5.0,
  reviewCount = 100,
  variant = "compact",
  className = "",
}: GoogleReviewsBadgeProps) => {
  const stars = Array.from({ length: 5 }, (_, i) => i < Math.floor(rating));

  if (variant === "compact") {
    return (
      <div
        className={`inline-flex items-center gap-2 bg-card border border-border rounded-full px-4 py-2 shadow-md animate-fade-in ${className}`}
      >
        {/* Google "G" Logo */}
        <svg viewBox="0 0 24 24" className="w-5 h-5" aria-label="Google">
          <path
            fill="#4285F4"
            d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
          />
          <path
            fill="#34A853"
            d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
          />
          <path
            fill="#FBBC05"
            d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
          />
          <path
            fill="#EA4335"
            d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
          />
        </svg>

        {/* Stars */}
        <div className="flex items-center gap-0.5">
          {stars.map((filled, i) => (
            <Star
              key={i}
              className={`w-4 h-4 ${
                filled ? "fill-accent text-accent" : "fill-muted text-muted"
              }`}
            />
          ))}
        </div>

        {/* Rating & Count */}
        <span className="text-sm font-semibold text-foreground">
          {rating.toFixed(1)}
        </span>
        <span className="text-sm text-muted-foreground">
          ({reviewCount}+ reviews)
        </span>
      </div>
    );
  }

  // Full variant - larger display
  return (
    <div
      className={`bg-card border border-border rounded-2xl p-6 shadow-lg text-center animate-fade-in ${className}`}
    >
      {/* Google Logo */}
      <div className="flex justify-center mb-4">
        <svg viewBox="0 0 24 24" className="w-10 h-10" aria-label="Google">
          <path
            fill="#4285F4"
            d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
          />
          <path
            fill="#34A853"
            d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
          />
          <path
            fill="#FBBC05"
            d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
          />
          <path
            fill="#EA4335"
            d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
          />
        </svg>
      </div>

      {/* Stars */}
      <div className="flex justify-center gap-1 mb-3">
        {stars.map((filled, i) => (
          <Star
            key={i}
            className={`w-6 h-6 ${
              filled ? "fill-accent text-accent" : "fill-muted text-muted"
            }`}
          />
        ))}
      </div>

      {/* Rating */}
      <div className="text-3xl font-bold text-foreground mb-1">
        {rating.toFixed(1)} / 5.0
      </div>

      {/* Review Count */}
      <p className="text-muted-foreground">
        Based on <span className="font-semibold text-foreground">{reviewCount}+</span> reviews
      </p>

      {/* Link */}
      <a
        href="https://search.google.com/local/writereview?placeid=ChIJGRnb8Y4hEW_aXz-ZmPk8nFM"
        target="_blank"
        rel="noopener noreferrer"
        className="inline-block mt-4 text-sm text-primary hover:text-primary/80 underline underline-offset-2"
      >
        Leave a review on Google
      </a>
    </div>
  );
});

GoogleReviewsBadge.displayName = "GoogleReviewsBadge";

export default GoogleReviewsBadge;
